import { useState, useEffect, useCallback } from 'react';

/**
 * useNotifications - Custom hook for notification CRUD operations.
 * @param {Object} options
 * @param {string|number} userId - The user/supplier/agent ID to filter notifications.
 * @param {string} role - The role context for notifications ("supplier", "user", "agent", etc).
 * @param {function} getValidToken - Function to get a valid auth token.
 * @param {boolean} enabled - If false, disables fetching on mount.
 */
const useNotifications = ({
    userId,
    role = 'user',
    getValidToken,
    enabled = true
}) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    // Helper to get the correct query param for the role
    const getRoleQueryParam = () => {
        switch (role) {
            case 'supplier':
                return `supplier_id=${userId}`;
            case 'user':
                return `user_id=${userId}`;
            case 'agent':
                return `agent_id=${userId}`;
            case 'admin':
                return `admin_id=${userId}`;
            default:
                return `user_id=${userId}`;
        }
    };

    // Helper to sort notifications: unread first (newest first), then read (newest first)
    const sortNotifications = (arr) => {
        return [...arr].sort((a, b) => {
            if (a.read === b.read) {
                // Sort by created_at/time descending
                const aTime = a.rawTime || a.time || '';
                const bTime = b.rawTime || b.time || '';
                // Try to parse as date, fallback to string compare
                const aDate = new Date(aTime);
                const bDate = new Date(bTime);
                if (!isNaN(aDate) && !isNaN(bDate)) {
                    return bDate - aDate;
                }
                return bTime.localeCompare(aTime);
            }
            // Unread first
            return a.read ? 1 : -1;
        });
    };

    // Fetch all notifications for the given role/user
    const fetchNotifications = useCallback(async () => {
        if (!userId) {
            setNotifications([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const token = getValidToken ? await getValidToken() : null;
            const url = `${API_BASE_URL}/api/notifications?${getRoleQueryParam()}`;
            const res = await fetch(url, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
            });
            if (!res.ok) throw new Error('Error al cargar notificaciones');
            const data = await res.json();
            const mapped = (Array.isArray(data) ? data : data.notifications || []).map(n => {
                let formattedTime = '';
                const rawTime = n.created_at || n.time || '';
                if (rawTime) {
                    try {
                        const dateObj = new Date(rawTime);
                        formattedTime = dateObj.toLocaleString('es-CL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });
                    } catch {
                        formattedTime = rawTime;
                    }
                }
                return {
                    id: n.id,
                    title: n.title,
                    message: n.message,
                    time: formattedTime,
                    rawTime: rawTime,
                    read: n.is_read,
                };
            });
            setNotifications(sortNotifications(mapped));
        } catch (err) {
            setError(err.message || 'Error desconocido');
        } finally {
            setLoading(false);
        }
    }, [userId, getValidToken, API_BASE_URL, role]);

    // Fetch on mount or when userId/role changes
    useEffect(() => {
        if (enabled) fetchNotifications();
    }, [fetchNotifications, enabled]);

    // Fetch a single notification by ID
    const fetchNotification = useCallback(
        async (id) => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                const url = `${API_BASE_URL}/api/notifications/${id}`;
                const res = await fetch(url, {
                    headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                });
                if (!res.ok) throw new Error('Error al cargar la notificación');
                const n = await res.json();
                let formattedTime = '';
                const rawTime = n.created_at || n.time || '';
                if (rawTime) {
                    try {
                        const dateObj = new Date(rawTime);
                        formattedTime = dateObj.toLocaleString('es-CL', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false
                        });
                    } catch {
                        formattedTime = rawTime;
                    }
                }
                return {
                    id: n.id,
                    title: n.title,
                    message: n.message,
                    time: formattedTime,
                    rawTime: rawTime,
                    read: n.is_read,
                };
            } catch (err) {
                setError(err.message || 'Error desconocido');
                return null;
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL]
    );

    // Create a notification
    const createNotification = useCallback(
        async (notificationData) => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                const url = `${API_BASE_URL}/api/notifications`;
                const res = await fetch(url, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify(notificationData),
                });
                if (!res.ok) throw new Error('Error al crear la notificación');
                const newNotification = await res.json();
                await fetchNotifications(); // Refresh list
                return newNotification;
            } catch (err) {
                setError(err.message || 'Error desconocido');
                return null;
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL, fetchNotifications]
    );

    // Mark one as read
    const markAsRead = useCallback(
        async (id) => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                const url = `${API_BASE_URL}/api/notifications/${id}`;
                const res = await fetch(url, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                    },
                    body: JSON.stringify({ is_read: true }),
                });
                if (!res.ok) throw new Error('Error al marcar como leída');
                setNotifications((prev) => sortNotifications(
                    prev.map((n) => (n.id === id ? { ...n, read: true } : n))
                ));
            } catch (err) {
                setError(err.message || 'Error desconocido');
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL]
    );

    // Mark all as read
    const markAllAsRead = useCallback(
        async () => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                await Promise.all(
                    notifications
                        .filter((n) => !n.read)
                        .map((n) =>
                            fetch(`${API_BASE_URL}/api/notifications/${n.id}`, {
                                method: 'PUT',
                                headers: {
                                    'Content-Type': 'application/json',
                                    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
                                },
                                body: JSON.stringify({ is_read: true }),
                            })
                        )
                );
                setNotifications((prev) => sortNotifications(
                    prev.map((n) => ({ ...n, read: true }))
                ));
            } catch (err) {
                setError(err.message || 'Error desconocido');
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL, notifications]
    );

    // Delete one
    const deleteNotification = useCallback(
        async (id) => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                const url = `${API_BASE_URL}/api/notifications/${id}`;
                const res = await fetch(url, {
                    method: 'DELETE',
                    headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                });
                if (!res.ok) throw new Error('Error al eliminar la notificación');
                setNotifications((prev) => sortNotifications(
                    prev.filter((n) => n.id !== id)
                ));
            } catch (err) {
                setError(err.message || 'Error desconocido');
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL]
    );

    // Delete all
    const deleteAllNotifications = useCallback(
        async () => {
            setLoading(true);
            setError(null);
            try {
                const token = getValidToken ? await getValidToken() : null;
                await Promise.all(
                    notifications.map((n) =>
                        fetch(`${API_BASE_URL}/api/notifications/${n.id}`, {
                            method: 'DELETE',
                            headers: token ? { 'Authorization': `Bearer ${token}` } : undefined,
                        })
                    )
                );
                setNotifications([]);
            } catch (err) {
                setError(err.message || 'Error desconocido');
            } finally {
                setLoading(false);
            }
        },
        [getValidToken, API_BASE_URL, notifications]
    );

    return {
        notifications,
        loading,
        error,
        fetchNotifications,
        fetchNotification,
        createNotification,
        markAsRead,
        markAllAsRead,
        deleteNotification,
        deleteAllNotifications,
        setNotifications, // for advanced use
    };
};

export default useNotifications;